import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL, fetchWithAuth, getImageUrl } from '../api';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import QuickRegisterModal from './modal/QuickRegisterModal';
import { 
  Calendar, MapPin, Users, BarChart2, Link as LinkIcon, ChevronDown, MoreHorizontal,
  Edit, Layers, ArrowLeft, Share2, ArrowRight, X, Inbox, Star, Music
} from 'react-feather';

// IMPORTACIÓN DE COMPONENTES DE PESTAÑAS
import EventTeamListView from '../components/EventDetails/EventTeamListView';
import EventHierarchy from '../components/EventDetails/EventHierarchy'
import EventLinksTab from '../components/EventDetails/EventLinksTab';
import EventMetricsTab from '../components/EventDetails/EventMetricsTab';
import EventEditTab from '../components/EventDetails/EventEditTab';
import EventRequestTab from '../components/EventDetails/EventRequestsTab';
import EventGuardAccess from '../components/EventDetails/EventGuardAccess';

// --- HELPER: Extraer color dominante y usarlo en caché ---
const useImageColor = (imageUrl, defaultColor = 'rgba(126, 126, 126, 0.2)') => {
  const [color, setColor] = useState(defaultColor);

  useEffect(() => {
    if (!imageUrl) { setColor(defaultColor); return; }

    const img = new Image();
    
    img.crossOrigin = "Anonymous"; // permiso para leer los bits de la imagen
    const src = getImageUrl(imageUrl);

    // Timestamp para que el nav use versión en cache con cabeceras CORS
    img.src = src + (src.includes('?') ? '&' : '?') + 'cache=' + new Date().getTime();

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 1; 
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        
        ctx.drawImage(img, 0, 0, 1, 1);
        const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
        
        setColor(`rgba(${r}, ${g}, ${b}, 0.7)`);
      } catch (e) {
        // Si S3 no permite CORS nos quedamos con el default.
        console.warn("No se pudo extraer color (Bloqueo CORS):", e);
        setColor(defaultColor);
      }
    };

    img.onerror = () => {
        setColor(defaultColor);   // Si la imagen no carga, volver al default
    };

  }, [imageUrl]); // Dependencia: si cambia la URL, recalcula

  return color;
};

export default function EventDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // ----- SearchParams: Para Promoter Link, así evitamos tener 2 .jsx -----
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // ----- STATES -----
  const [event, setEvent] = useState(null);
  const [qrStatus, setQrStatus] = useState(null);
  const [attendingUsers, setAttendingUsers] = useState([]);
  const [totalAttending, setTotalAttending] = useState(0);

  const [loading, setLoading] = useState(true);
  const [requestLoading, setRequestLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('evento');

  // Estados Modal
  const [showQRPanel, setShowQRPanel] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState('');
  const [qrRequestError, setQrRequestError] = useState('');
  const [qrRequestLoading, setQrRequestLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // --- Estado del Promotor --- 
  const [promoter, setPromoter] = useState(null);
  const [promoterCode, setPromoterCode] = useState(null); // Para guardar el ref

  // --- LÓGICA DE PERMISOS ---
  const userRole = event?.current_user_role;
  const [isCreator, setIsCreator] = useState(false);
  const isAdmin = user?.role === 'admin';
  const isCollaborator = userRole !== null && userRole !== undefined;

  // --- QUICK LOGIN ---
  const [showQuickModal, setShowQuickModal] = useState(false);
  const [isQuickLoading, setIsQuickLoading] = useState(false);

  useEffect(() => {
    const urlRef = searchParams.get('ref');
    if (urlRef && !user) {
        const timer = setTimeout(() => setShowQuickModal(true), 1200);
        return () => clearTimeout(timer);
    }
  }, [searchParams, user]);

  const handleQuickRegister = async (data) => {
    setIsQuickLoading(true);
    try {
        // Aquí llamas a tu fetch (puedes usar el que te pasé en la respuesta anterior)
        console.log("Enviando datos:", data);
        // ... tu lógica de fetch ...
        setShowQuickModal(false);
    } catch (error) {
        console.error(error);
    } finally {
        setIsQuickLoading(false);
    }
  };

  const isSocio = userRole === 'Socio' || event?.current_user_depth === 1;

  const canManage = isAdmin || isCreator || isCollaborator;
  const canFullManage = isAdmin || isCreator || isSocio;

  const getYoutubeEmbed = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) 
    ? `https://www.youtube.com/embed/${match[2]}` 
    : null;
};

  // --- Vista del Evento (Resumen) ---
  const EventOverview = ({ event, attendingUsers, totalAttending, onRequest, qrStatus, loadingRequest }) => {
    const eventDate = parseISO(event.date);
    const glowColor = useImageColor(event.image_url);
    const embedVideoUrl = getYoutubeEmbed(event.video); // <--- Procesamos el video

    return (
      <div className="flex flex-col lg:grid lg:grid-cols-2 min-h-screen relative">
        
        {/* --- COLUMNA IMAGEN (Izquierda/Arriba) --- */}
        <div className="order-1 lg:order-2 relative h-[45vh] lg:h-screen lg:sticky lg:top-0 bg-zinc-900 border-l border-white/5 overflow-hidden">
          <div 
              className="absolute inset-0 blur-[120px] z-0 transition-colors duration-1000" 
              style={{ backgroundColor: glowColor }}
          />
          <div className="absolute inset-0 flex items-center justify-center p-0 lg:p-12 z-10">
              <div className="relative w-full h-full lg:h-auto lg:w-full lg:max-w-md lg:aspect-[3/4] lg:rounded-2xl overflow-hidden shadow-2xl group lg:border border-white/10">
                  <img 
                      src={getImageUrl(event.image_url)} 
                      alt="Poster" 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent lg:bg-gradient-to-tr lg:from-white/20 lg:to-transparent opacity-60 pointer-events-none" />
              </div>
          </div>
        </div>

        {/* --- COLUMNA CONTENIDO (Derecha/Abajo) --- */}
        <div className="order-2 lg:order-1 p-6 md:p-12 lg:p-16 flex flex-col gap-10 pb-32 relative z-10">
          
          {/* Banner Promotor (Existente) */}
          {promoter && (
             // ... (tu código del promotor se mantiene igual)
             <div className="animate-in fade-in slide-in-from-top-2 duration-500 mb-2">
                <div className="inline-flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-full pl-1.5 pr-4 py-1.5 backdrop-blur-sm hover:bg-white/10 transition-colors">
                    <div className="w-6 h-6 rounded-full bg-[#1a1a1a] border border-white/10 flex items-center justify-center text-[10px] text-white font-medium overflow-hidden shrink-0">
                        {promoter.profile_image_url ? (
                            <img src={getImageUrl(promoter.profile_image_url)} className="w-full h-full object-cover" alt={promoter.name} />
                        ) : (
                            /* Fallback a la primera letra del nombre en vez de la estrella */
                            <span>{promoter.name.charAt(0).toUpperCase()}</span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 text-sm">
                        <span className="text-gray-400">Promotor: </span>
                        <span className="text-white font-medium">{promoter.name}</span>
                    </div>
                </div>
            </div>
          )}

          {/* Header */}
          <div className="space-y-6 pt-4 lg:pt-8"> 
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-sm font-bold text-white uppercase overflow-hidden">
                  {event.creator?.profile_image_url ? (
                      <img src={getImageUrl(event.creator.profile_image_url)} alt="Creator" className="w-full h-full object-cover"/>
                  ) : (
                      event.creator?.name?.charAt(0) || 'A'
                  )}
              </div>
              <div>
                  <p className="text-sm font-semibold text-white">{event.creator_name || 'Organizador'}</p>
                  <p className="text-xs text-white/50">Presenta</p>
              </div>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight leading-[1.1]">
              {event.name}
            </h1>

            {/* --- NUEVO: TAGS --- */}
            {event.tags && event.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {event.tags.map((tag, i) => (
                  <span key={i} className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border border-white/20 text-white/80 bg-white/5">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Info Básica */}
          <div className="grid gap-6 border-y border-white/10 py-8">
              <div className="flex items-start gap-4">
                  <div className="p-3 bg-white/5 rounded-xl text-white"><Calendar size={20} /></div>
                  <div>
                      <p className="text-lg font-semibold text-white">
                          {format(eventDate, "EEEE, d 'de' MMMM", { locale: es })}
                      </p>
                      <p className="text-white/50">{event.time} hrs - Hasta el amanecer</p>
                  </div>
              </div>

              <div className="flex items-start gap-4">
                  <div className="p-3 bg-white/5 rounded-xl text-white"><MapPin size={20} /></div>
                  <div>
                      <p className="text-lg font-semibold text-white">{event.location}</p>
                      <p className="text-white/50">{event.direction || 'Lima, Perú'}</p>
                  </div>
              </div>
          </div>

          {/* --- NUEVO LINEUP PREMIUM --- */}
          <div className=" bg-black p-8 flex items-center justify-center">
            <div className="w-full max-w-2xl">
                {/* Header */}
                <div className="mb-8 flex items-center gap-3">
                <div className="h-6 w-1 bg-gradient-to-b from-purple-500 to-transparent"></div>
                <h3 className="text-sm font-medium text-white/40 uppercase tracking-[0.2em]">
                    Lineup del Evento
                </h3>
                <span className="ml-auto text-xs text-white/30">
                    {event.lineup.length} artistas
                </span>
                </div>

                {/* Lista de Artistas */}
                <div className="space-y-1">
                {event.lineup.map((artist, index) => (
                    <div
                    key={index}
                    className="group relative flex items-center gap-6 px-6 py-5 rounded-lg hover:bg-white/[0.02] transition-all duration-300 border-b border-white/[0.05] last:border-0"
                    >
                    {/* Vinilo decorativo (solo visible en hover) */}
                    <div className="absolute -right-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none">
                        <img 
                        src="/vinilo.png" 
                        alt="" 
                        className="w-32 h-32 group-hover:rotate-[360deg] transition-transform duration-[3s]"
                        />
                    </div>

                    {/* Hora */}
                    <div className="text-right min-w-[60px]">
                        <span className="font-mono text-base text-white/90 tabular-nums">
                        {artist.time || 'TBA'}
                        </span>
                    </div>

                    {/* Separador */}
                    <div className="h-10 w-px bg-white/10 group-hover:bg-purple-500/30 transition-colors"></div>

                    {/* Avatar minimalista */}
                    <div className="relative">
                        <div className="w-10 h-10 rounded-full border border-white/20 bg-white/5 flex items-center justify-center group-hover:border-purple-500/50 group-hover:bg-purple-500/10 transition-all duration-300">
                        <span className="text-xs font-bold text-white/70 group-hover:text-purple-300 transition-colors">
                            {artist.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </span>
                        </div>
                        {/* Dot indicator */}
                        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-purple-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                        <h4 className="text-lg font-semibold text-white/95 mb-0.5 group-hover:text-white transition-colors">
                        {artist.name}
                        </h4>
                        <p className="text-[10px] text-white/30 uppercase tracking-widest font-medium">
                        {artist.role}
                        </p>
                    </div>

                    {/* Icono sutil */}
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <Music size={16} className="text-white/30" />
                    </div>
                    </div>
                ))}
                </div>
            </div>
            </div>

          {/* Asistentes (Existente) */}
          {/* ... (Tu código de asistentes se mantiene igual) ... 
           <div>
              <div className="flex justify-between items-end mb-4">
                  <h3 className="text-white font-medium">Asistirán</h3>
                  {totalAttending > 0 && <span className="text-xs text-white/40">Ver las {totalAttending} personas en lista.</span>}
              </div>

              <div className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/5">
                 ... lógica de asistentes ... 
                
                {totalAttending === 0 ? (
                      <p className="text-sm text-gray-400">Sé el primero en apuntarte.</p>
                  ) : (
                      <>
                          <div className="flex overflow-hidden pl-1">
                              {attendingUsers.slice(0, 6).map((user) => (
                                  <div 
                                      key={user.id} 
                                      className="w-10 h-10 rounded-full border-2 border-[#111] bg-zinc-800 flex items-center justify-center text-xs text-white/50 overflow-hidden cursor-pointer hover:scale-110 transition-transform hover:z-10 relative"
                                      title={user.name}
                                  >
                                      {user.profile_image_url ? (
                                          <img src={getImageUrl(user.profile_image_url)} alt={user.username} className="w-full h-full object-cover"/>
                                      ) : (
                                          <span className="font-bold text-white">{user.name?.charAt(0)}</span>
                                      )}
                                  </div>
                              ))}
                              {totalAttending > 6 && (
                                  <div className="w-10 h-10 rounded-full border-2 border-[#111] bg-zinc-700 flex items-center justify-center text-xs font-bold text-white">
                                      +{totalAttending - 6}
                                  </div>
                              )}
                          </div>
                          <div className="text-sm text-white/60 pl-2">
                              {attendingUsers[0]?.name?.split(' ')[0]} 
                              {totalAttending > 1 && <> y <strong className="text-white">{totalAttending - 1} más</strong></>} van a ir
                          </div>
                      </>
                  )} 
              </div>
          </div>*/}

          {/* Descripción */}
          <div className="space-y-4">
              <h3 className="text-xl font-bold text-white">Sobre el evento</h3>
              <div className="prose prose-invert text-lg text-white/70 leading-relaxed whitespace-pre-line">
                  {event.description || "Sin descripción disponible."}
              </div>
          </div>

          {/* --- NUEVO: VIDEO SECTION --- */}
          {embedVideoUrl && (
            <div className="w-full aspect-video rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black">
              <iframe
                width="100%"
                height="100%"
                src={embedVideoUrl}
                title="Event Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          )}

          {/* Mapa (Modificado ligeramente el encodeURIComponent para evitar errores en src) */}
            <div className="w-full mb-16 h-96 rounded-2xl overflow-hidden border border-white/10 filter grayscale invert contrast-125 opacity-80 hover:opacity-100 transition-all">
                <iframe 
                width="100%" 
                height="100%" 
                frameBorder="0" 
                src={`https://maps.google.com/maps?q=${encodeURIComponent((event.direction || '') + ', ' + (event.location || ''))}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                title={event.name}
                ></iframe>
            </div>

          {/* Bottom Bar (Solicitar Entrada) */}
           <div className="fixed bottom-6 left-6 right-6 lg:left-[calc(16rem+2rem)] lg:w-[calc(50vw-4rem)] z-50">
              {qrStatus ? (
                  <button 
                      onClick={() => onRequest('view')}
                      className={`w-full font-bold text-lg py-4 rounded-xl shadow-2xl hover:scale-[1.02] transition-transform flex items-center justify-center gap-2
                      ${qrStatus.status === 'approved' ? 'bg-white text-black' : 'bg-yellow-500 text-black'}`}
                  >
                      {qrStatus.status === 'approved' ? 'Ver Entrada' : 'Solicitud Pendiente'} <ArrowRight size={20} />
                  </button>
              ) : (
                  <button 
                      onClick={() => onRequest('create')}
                      disabled={loadingRequest}
                      className="w-full bg-white text-black font-bold text-lg py-4 rounded-xl shadow-2xl hover:scale-[1.02] transition-transform flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                      {loadingRequest ? 'Procesando...' : 'Solicitar Entrada'} <ArrowRight size={20} />
                  </button>
              )}
          </div>
        </div>
      </div>
    );
  };

  // ==========================================================
  //        FIN DE LA PARTE VISUAL DE LA PAGINA
  // ==========================================================

  // --- EFECTO PARA DETECTAR Y GUARDAR PROMOTOR ---
  useEffect(() => {
    const fetchPromoterInfo = async (ref) => {
        try {
            let userIdForFetch = null;
            // Intentar decodificar localmente para respuesta inmediata (Nombre)
            try {
                const decoded = atob(ref.replace(/-/g, '+').replace(/_/g, '/'));
                const promoterData = JSON.parse(decoded);

                setPromoter({ name: promoterData.n }); // Datos mínimos
                userIdForFetch = promoterData.uid;
            } catch (e) {
                // Fallback a la API si no es base64
                userIdForFetch = ref;
            }

            if (userIdForFetch) {
                const response = await fetch(`${API_URL}/users/${userIdForFetch}/name`);
                if (response.ok) {
                    const data = await response.json();
                    setPromoter(data);
                }
            }
            setPromoterCode(ref);
        } catch (err) {
            console.error("Error fetching promoter", err);
        }
    };

    const urlRef = searchParams.get('ref');
    
    if (urlRef) {
        localStorage.setItem('promoter_ref', urlRef); // Persistir
        fetchPromoterInfo(urlRef);
    } else {
        const storedRef = localStorage.getItem('promoter_ref');
        if (storedRef) {
            fetchPromoterInfo(storedRef);
        }
    }
  }, [searchParams]);

  // Effect: Cargar grupos
  useEffect(() => {
    if (showQRPanel && user) {
      setGroupsLoading(true);
      setGroupsError('');
      fetchWithAuth(`${API_URL}/groups`)
        .then(setGroups)
        .catch(() => setGroupsError('Error al cargar tus grupos'))
        .finally(() => setGroupsLoading(false));
    }
  }, [showQRPanel, user]);

  // Effect: Carga inicial de datos (EventData - AttendingUsers - QRdata)
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const eventData = await fetchWithAuth(`${API_URL}/events/${id}`);
        setEvent(eventData);
        setIsCreator(user && (eventData.creator_id === user.id || user.role === 'admin'));

        try {
            const response = await fetch(`${API_URL}/events/${id}/attending-users`);
            if (response.ok) {
                const data = await response.json();
                setAttendingUsers(data.data);
                setTotalAttending(data.total_attending);
            } else {
                 setAttendingUsers([]);
                 setTotalAttending(0);
            }
        } catch (e) {
            console.warn("No se pudieron cargar asistentes", e);
        }

        if (user) {
          try {
            const qrData = await fetchWithAuth(`${API_URL}/events/${id}/qr`);
            setQrStatus(qrData);
          } catch (e) {
            setQrStatus(null);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [id, user]);

  // Contador de Solicitudes no aprobadas (en esta página es para el puntito rojo en Solicitudes)
  useEffect(() => {
    const fetchPendingCount = async () => {
        if (!canManage) return; 

        try {
          const data = await fetchWithAuth(`${API_URL}/events/${id}/qr-requests`);
          const count = Array.isArray(data) ? data.filter(r => !r.approved).length : 0;
          setPendingCount(count);
        } catch (err) {
          console.error("Error contando solicitudes", err);
        }
    };

    if (canManage) {
        fetchPendingCount();
    }
  }, [canManage, id]); 

  // 5. Redirect si intenta solicitar y no está logueado
  const handleQRAction = async (action) => {
    if (action === 'view') { navigate(`/qr/${event.id}`); return; }
    if (!user) { 
        localStorage.setItem('redirectAfter', location.pathname + location.search);
        navigate('/login'); 
        return; 
    }
    if (action === 'create') {
        setShowQRPanel(true);
    }
  };

  // Menu Items (Igual)
  const menuItems = [
    { id: 'evento', label: 'Evento', icon: Layers },
    ...(canManage ? [
      { id: 'equipo', label: 'Equipo', icon: Users },
      { id: 'organigrama', label: 'Organigrama', icon: Users },
    ] : []),
    ...(canFullManage ? [
      { id: 'solicitudes', label: 'Solicitudes', icon: Inbox, badge: pendingCount > 0 ? pendingCount : null },
      { id: 'links', label: 'Links', icon: LinkIcon },
      { id: 'metricas', label: 'Métricas', icon: BarChart2 },
      { id: 'guardias', label: 'Guardias', icon: Users },
      { id: 'editar', label: 'Editar', icon: Edit },
    ] : [])
  ];

  // LOS RETURNS (Early Returns)
  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center text-white">Cargando...</div>;
  if (!event) return <div className="min-h-screen bg-black flex items-center justify-center text-white">Evento no encontrado</div>;

  return (
    <div className="flex min-h-screen bg-black text-white font-sans">
      
      {/* SIDEBAR */}
      <aside className="w-64 mt-16 h-screen sticky top-0 border-r border-white/10 flex flex-col p-3 hidden md:flex bg-black z-30">

        <div className="space-y-1 flex-1">
          
          {menuItems.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-150 text-sm font-medium relative
                  ${isActive 
                    ? 'bg-white/10 text-white' 
                    : 'text-zinc-500 hover:bg-white/5 hover:text-zinc-200'
                  }`}
              >
                <item.icon size={16} strokeWidth={isActive ? 2 : 1.5} />
                <span className="flex-1 text-left">{item.label}</span>
                
                {item.badge && (
                  <span className="flex items-center justify-center min-w-[18px] h-4 px-1 text-[10px] font-bold rounded-full bg-zinc-800 text-zinc-400 border border-white/5">
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-auto pt-4 border-t border-white/5">
            <div className="flex items-center justify-between px-2 py-1 hover:bg-white/5 rounded-lg transition-colors cursor-pointer group">
            <div className="flex items-center gap-3 overflow-hidden">
                <div className="w-7 h-7 rounded-full bg-zinc-800 border border-white/10 flex-shrink-0 flex items-center justify-center">
                {user?.profile_image_url ? (
                    <img src={getImageUrl(user.profile_image_url)} alt="Me" className="w-full h-full object-cover rounded-full"/>
                ) : (
                    <span className="font-medium text-zinc-400 text-xs">{user?.name?.charAt(0)}</span>
                )}
                </div>
                <div className="overflow-hidden">
                <p className="text-sm font-medium text-zinc-300 truncate">{user?.name || 'martin.perezbonany...'}</p>
                </div>
            </div>
            <MoreHorizontal size={14} className="text-zinc-600 group-hover:text-zinc-400" />
            </div>
        </div>
    </aside>

      {/* ÁREA PRINCIPAL */}
      <main className="flex-1 min-w-0 bg-[#050505] relative">
        
        {/* --- HEADER MÓVIL + NAVEGACIÓN --- */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur border-b border-white/10 transition-all">
           
           {/* 1. Top Bar (Título y Botones) */}
           <div className="flex items-center justify-between p-4">
              <button onClick={() => navigate('/')} className="p-2 -ml-2 hover:bg-white/10 rounded-full transition-colors">
                <ArrowLeft className="text-white" size={20}/>
              </button>
              <span className="font-bold text-white truncate px-2 text-sm flex-1 text-center">
                {event.name}
              </span>
              <button className="p-2 -mr-2 hover:bg-white/10 rounded-full transition-colors">
                <Share2 className="text-white" size={20}/>
              </button>
           </div>

           {/* 2. Barra de Navegación Horizontal (Solo visible si hay menú) */}
           <div className="flex overflow-x-auto no-scrollbar px-2 pb-0 w-full select-none">
              {menuItems.map((item) => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                        setActiveTab(item.id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`
                      flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all relative
                      ${isActive 
                        ? 'border-white text-white' 
                        : 'border-transparent text-white/50 hover:text-white/80'
                      }
                    `}
                  >
                    <item.icon size={16} />
                    {item.label}
                    
                    {/* Badge (Puntito rojo o número) */}
                    {item.badge && (
                       <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isActive ? 'bg-red-500 text-white' : 'bg-red-500/80 text-white'}`}>
                         {item.badge}
                       </span>
                    )}
                  </button>
                )
              })}
           </div>
        </div>

        {/* --- CONTENIDO --- */}
        <div className="md:pt-0 pt-[110px]"> 
            {activeTab === 'evento' ? (
                <EventOverview 
                    event={event} 
                    attendingUsers={attendingUsers}
                    totalAttending={totalAttending}
                    onRequest={handleQRAction}
                    qrStatus={qrStatus}
                    loadingRequest={requestLoading}
                />
            ) : (
                <div className="p-6 md:p-12 min-h-screen">
                     {/* Título de la sección en Móvil para dar contexto */}
                     <h2 className="md:hidden text-2xl font-bold text-white mb-6 flex items-center gap-3">
                        {menuItems.find(i => i.id === activeTab)?.icon && (() => {
                            const Icon = menuItems.find(i => i.id === activeTab).icon;
                            return <Icon className="text-purple-500" />;
                        })()}
                        {menuItems.find(i => i.id === activeTab)?.label}
                     </h2>

                     {/* Componentes de Pestañas */}
                        {canFullManage && activeTab === 'solicitudes' && (
                            <EventRequestTab eventId={id} />
                        )}
                        
                        {canManage && activeTab === 'equipo' && (
                            <EventTeamListView eventId={id} currentUserId={user?.id} isCreator={isCreator} />
                        )}

                        {canManage && activeTab === 'organigrama' && (
                            <EventHierarchy eventId={id} currentUserId={user?.id} isCreator={isCreator} />
                        )}
        
                        {canFullManage && activeTab === 'links' && (
                            <EventLinksTab eventId={id} isCreator={isCreator} />
                        )}

                        {canFullManage && activeTab === 'guardias' && (
                            console.log(event),
                            <EventGuardAccess event={event} />
                        )}
        
                        {canFullManage && activeTab === 'metricas' && (
                            <EventMetricsTab eventId={id} />
                        )}
        
                        {canFullManage && activeTab === 'editar' && (
                            <EventEditTab 
                                eventId={id} 
                                onEventUpdated={(updated) => setEvent(updated)}
                            />
                        )}
                </div>
            )}
        </div>
      </main>

        {/* MODAL DE SOLICITUD DE QR (REDDISEÑADO) */}
      {showQRPanel && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-200">
            <div className="relative bg-[#121212] border border-white/10 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden flex flex-col animate-slideUp">
                
                {/* Header del Modal */}
                <div className="p-6 border-b border-white/10 flex justify-between items-center bg-white/5">
                    <div>
                        <h3 className="text-xl font-bold text-white tracking-wide">Obtener Ticket</h3>
                        <p className="text-xs text-gray-400 mt-1">Selecciona cómo quieres asistir</p>
                    </div>
                    <button
                        className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full"
                        onClick={() => setShowQRPanel(false)}
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[70vh]">
                    
                    {/* Error Message */}
                    {qrRequestError && (
                        <div className="w-full mb-6 p-3 bg-red-500/10 border border-red-500/20 text-red-300 text-sm rounded-xl text-center">
                            {qrRequestError}
                        </div>
                    )}

                    {/* SECCIÓN 1: GRUPOS (PRIORIDAD ALTA) */}
                    <div className="mb-8">
                        <div className="flex items-center gap-2 mb-4">
                            <Users size={16} className="text-purple-400" />
                            <span className="text-sm font-bold text-white uppercase tracking-wider">Ir en Grupo (Recomendado)</span>
                        </div>

                        {groupsLoading ? (
                            <div className="py-8 text-center bg-white/5 rounded-xl border border-dashed border-white/10">
                                <span className="text-gray-500 text-sm animate-pulse">Cargando tus grupos...</span>
                            </div>
                        ) : groupsError ? (
                            <div className="text-red-400 text-center text-sm py-4">{groupsError}</div>
                        ) : groups.length === 0 ? (
                            <div className="text-center py-6 px-4 bg-white/5 rounded-xl border border-white/5">
                                <p className="text-gray-400 text-sm mb-2">No perteneces a ningún grupo aún.</p>
                                <p className="text-xs text-gray-500">Únete a uno para entrar más rápido con tus amigos.</p>
                            </div>
                        ) : (
                            <div className="grid gap-3">
                                {groups.map(group => (
                                    <button
                                        key={group.id}
                                        disabled={qrRequestLoading}
                                        onClick={async () => {
                                            if (!group.members || !Array.isArray(group.members)) return;
                                            
                                            console.log("🔵 CLICK EN GRUPO:", group.name);
                                            setQrRequestLoading(true);
                                            setQrRequestError('');
                                            
                                            try {
                                                const usernames = group.members.map(m => m.username);

                                                // 1. OBTENER REF DE LA URL ACTUAL
                                                const searchParams = new URLSearchParams(window.location.search);
                                                const refCode = searchParams.get('ref');

                                                // 2. CONSTRUIR URL CORRECTA
                                                let endpointUrl = `${API_URL}/events/${id}/request-qr-group`;
                                                if (refCode) {
                                                    endpointUrl += `?ref=${encodeURIComponent(refCode)}`;
                                                }
                                                
                                                // 3. LLAMADA AL BACKEND (USANDO endpointUrl)
                                                // --- CORRECCIÓN AQUÍ: Antes usabas el string hardcodeado ---
                                                const response = await fetchWithAuth(
                                                    endpointUrl, 
                                                    {
                                                        method: 'POST',
                                                        body: JSON.stringify({ usernames })
                                                    }
                                                );

                                                console.log("🟢 RESPUESTA BACKEND (GRUPO):", response);

                                                localStorage.setItem('lastEventName', event.name);
                                                const encodedEventName = encodeURIComponent(event.name);
                                                
                                                // 4. VERIFICACIÓN Y REDIRECCIÓN
                                                if(response.approved === true || response.status === 'approved'){
                                                    console.log("--> Redirigiendo a QR (Aprobado inmediato)");
                                                    navigate(`/qr/${id}`);
                                                } else {
                                                    console.log("--> Redirigiendo a Confirmación (Pendiente)");
                                                    navigate(`/qr-request-confirmation/${encodedEventName}`, {
                                                        state: { prevPath: location.pathname }
                                                    });
                                                }

                                            } catch (err) {
                                                console.error("🔴 ERROR EN GRUPO:", err);
                                                setQrRequestError(typeof err.message === 'string' ? err.message : 'Error al solicitar QR');
                                                setQrRequestLoading(false);
                                            }
                                        }}
                                        className="group relative w-full p-4 bg-gradient-to-r from-purple-900/20 to-blue-900/20 hover:from-purple-900/40 hover:to-blue-900/40 border border-white/10 hover:border-white/30 rounded-xl transition-all text-left flex items-center justify-between overflow-hidden"
                                    >
                                        <div className="relative z-10">
                                            <span className="block font-bold text-white text-lg group-hover:translate-x-1 transition-transform">{group.name}</span>
                                            <span className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                                                <Users size={12} /> {group.members?.length || 0} miembros
                                            </span>
                                        </div>
                                        <div className="relative z-10 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white text-white group-hover:text-black transition-all">
                                            <ArrowRight size={16} />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* SEPARADOR */}
                    <div className="relative flex py-2 items-center mb-6">
                        <div className="flex-grow border-t border-white/10"></div>
                        <span className="flex-shrink-0 mx-4 text-gray-600 text-xs uppercase">O también puedes</span>
                        <div className="flex-grow border-t border-white/10"></div>
                    </div>

                    {/* SECCIÓN 2: INDIVIDUAL (SECUNDARIO) */}
                    <button
                        disabled={qrRequestLoading || qrStatus}
                        className={`
                            w-full py-4 px-6 rounded-2xl font-bold text-base transition-all duration-200
                            flex items-center justify-center gap-2 active:scale-[0.98]
                            ${(qrRequestLoading || qrStatus) 
                                ? 'bg-white/10 text-gray-500 cursor-not-allowed' 
                                : 'bg-white text-black hover:bg-gray-100 shadow-[0_0_20px_rgba(255,255,255,0.1)]'}
                        `}
                        onClick={async () => {
                            if (qrStatus) return;
                            
                            console.log("🔵 CLICK EN INDIVIDUAL");
                            setQrRequestLoading(true);
                            setQrRequestError('');
                            
                            try {
                                // 1. OBTENER EL REF (Si existe)
                                const searchParams = new URLSearchParams(window.location.search);
                                const refCode = searchParams.get('ref'); 

                                // 2. CONSTRUIR URL
                                let endpointUrl = `${API_URL}/events/${id}/request-qr`;
                                
                                // Si hay refCode, lo pegamos.
                                if (refCode) {
                                    endpointUrl += `?ref=${encodeURIComponent(refCode)}`;
                                }

                                // 3. CAPTURAMOS RESPUESTA (Usando endpointUrl)
                                const response = await fetchWithAuth(endpointUrl, { method: 'POST' });
                                
                                console.log("🟢 RESPUESTA BACKEND:", response);
                                
                                localStorage.setItem('lastEventName', event.name);
                                const encodedEventName = encodeURIComponent(event.name);
                                
                                // 4. VERIFICACIÓN Y REDIRECCIÓN
                                if(response.approved === true || response.status === 'approved'){
                                    navigate(`/qr/${id}`);
                                } else {
                                    navigate(`/qr-request-confirmation/${encodedEventName}`, {
                                        state: { prevPath: location.pathname }
                                    });
                                }

                            } catch (err) {
                                console.error("🔴 ERROR:", err);
                                setQrRequestError(typeof err.message === 'string' ? err.message : 'Error al solicitar QR');
                                setQrRequestLoading(false);
                            } 
                        }}
                    >
                        {qrRequestLoading ? (
                            'Procesando...'
                        ) : qrStatus?.status === 'approved' ? (
                            <>Ticket Ya Aprobado</>
                        ) : qrStatus?.status === 'pending' ? (
                            <>Solicitud Pendiente</>
                        ) : (
                            <>
                                <span>Solicitar solo para mí</span>
                            </>
                        )}
                    </button>
                    
                </div>
            </div>
            
            <style>{`
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-slideUp {
                    animation: slideUp 0.3s ease-out forwards;
                }
            `}</style>
        </div>
      )}

      <QuickRegisterModal 
        isOpen={showQuickModal}
        onClose={() => setShowQuickModal(false)}
        onSubmit={handleQuickRegister}
        loading={isQuickLoading}
        eventName={event?.name}
      />

    </div>
  );
}